import Configuration from 'configuration/configuration';
import { OidcPrompt, OpenIDResponseMode, OpenIdResponseType } from 'shared-types';
import { encodeState } from 'shared-types/auth';
import { v4 as uuidv4 } from 'uuid';

export interface MakeLinkProps {
  url: string;
  idProvider: number;
  clientId: string;
  redirectTo?: string;
  scope?: string;
  response_type?: OpenIdResponseType;
  response_mode?: OpenIDResponseMode;
  loginHint?: string;
  prompt?: OidcPrompt;
}

export function buildState(id: number, redirectTo?: string) {
  return encodeState(uuidv4(), id.toString(), redirectTo ?? '');
}

/**
 * Generates a URL link with query parameters for initiating an OIDC authentication flow.
 */
export const makeLink = ({
  idProvider,
  url,
  clientId,
  redirectTo,
  scope = '',
  loginHint,
  response_mode = 'query',
  response_type = 'code',
  prompt,
}: MakeLinkProps): { url: string; nonce: string; state: string } => {
  const state = buildState(idProvider, redirectTo);
  const nonce = uuidv4();

  const queryString = new URLSearchParams({
    nonce,
    state,
    scope,
    prompt: prompt ?? 'consent',
    redirect_uri: `${window.location.origin}/callback`,
    response_type,
    response_mode,
    client_id: clientId,
    access_type: 'offline',
  });

  if (loginHint) {
    queryString.append('login_hint', loginHint);
  }

  const finalUrl = `${url}?${decodeURIComponent(queryString.toString())}`;

  return { url: finalUrl, nonce, state };
};

/**
 * Decodes the provided state, which is a base64-encoded string containing relevant information.
 * Splits the state into parts and returns an object with the decoded values.
 */
export function decodeState(state: string) {
  const [uuidState, oidcId, redirectTo] = window.atob(state).split('#');

  return {
    uuidState,
    oidcId: Number(oidcId),
    redirectTo,
  };
}

interface GetImplicitFlowParamsReturn {
  access_token?: string;
  token_type?: string;
  expires_in?: string;
  id_token?: string;
  scope?: string;
  state?: string;
}

/**
 * Extract the parameters from the url fragment during implicit flow authentication.
 * @returns A decoded object containing the access_token, token_type, expires_in, id_token, scope, and state
 */
export function getImplicitFlowParams(): GetImplicitFlowParamsReturn {
  const params = window.location.hash.substring(1);

  const searchParams = new URLSearchParams(params);

  return {
    access_token: searchParams.get('access_token') ?? undefined,
    token_type: searchParams.get('token_type') ?? undefined,
    expires_in: searchParams.get('expires_in') ?? undefined,
    id_token: searchParams.get('id_token') ?? undefined,
    scope: searchParams.get('scope') ?? undefined,
    state: searchParams.get('state') ?? undefined,
  };
}

/**
 * Verify if the authentication flow is implicit
 * @returns true if is implicit flow
 */
export const isImplicitFlow = (): boolean => !!window.location.href.match(/access_token/);

export type Validators = {
  hasLowerCase?: string;
  hasUpperCase?: string;
  hasNumber?: string;
  hasSpecialCharOrSpace?: string;
  hasValidLength?: string;
  noLeadingOrTrailingSpace?: string;
};

/**
 * Validates the given password and returns an object with validation errors.
 *
 * @param password - The password to be validated.
 * @return An object containing validation errors for the password.
 */
export function validatePassword(password: string) {
  const validators: Validators = {};

  if (password.length === 0) {
    return validators;
  }

  if (!/[a-z]/.test(password)) {
    validators.hasLowerCase = 'Senha deve conter pelo menos uma letra minúscula';
  }

  if (!/[A-Z]/.test(password)) {
    validators.hasUpperCase = 'Senha deve conter pelo menos uma letra maiúscula';
  }

  if (!/\d/.test(password)) {
    validators.hasNumber = 'Senha deve conter pelo menos um número';
  }

  if (!/[\W\s]/.test(password)) {
    validators.hasSpecialCharOrSpace = 'Senha deve conter pelo menos um caractere especial';
  }

  if (password.length < 8) {
    validators.hasValidLength = 'Senha deve conter pelo menos 8 caracteres';
  }

  if (!/^\S.*\S$/.test(password)) {
    validators.noLeadingOrTrailingSpace = 'Senha não pode começar ou terminar com espaços';
  }

  return validators;
}

export const FRONTEND_DOMAIN = Configuration.frontendUrl.replace(/https?:\/\//, '');

export function validateDomainURL(url: string, domain: string) {
  const escapedDomain = domain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regexPattern = new RegExp(
    `^(https?://)?([a-zA-Z0-9.-]+.)?(${escapedDomain})(:[0-9]{1,5})?([/?].*)?$`,
  );
  return regexPattern.test(url);
}
